import type { DivisionProfile, DivisionBaseline } from '@/lib/types/division-profile';
import { deriveTraitOutcomeRows } from '@/lib/analysis/division-briefing-traits';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface DivisionTraitsExplainerDialogProps {
  profile: DivisionProfile;
  baseline: DivisionBaseline;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function OutcomeBlock({
  title,
  items,
  variant,
}: {
  title: string;
  items: { label: string; detail: string; meta?: string }[];
  variant: 'strength' | 'weakness' | 'neutral';
}) {
  const titleClass =
    variant === 'strength'
      ? 'text-emerald-600 dark:text-emerald-400'
      : variant === 'weakness'
        ? 'text-red-600 dark:text-red-400'
        : 'text-muted-foreground';

  return (
    <div className="space-y-2">
      <h4 className={cn('text-xs font-semibold uppercase tracking-wide', titleClass)}>{title}</h4>
      <ul className="space-y-2.5 text-sm">
        {items.map((item, i) => (
          <li key={`${item.label}-${i}`} className="border-b border-border/60 pb-2 last:border-0 last:pb-0">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
              <p className="font-medium text-foreground">{item.label}</p>
              {item.meta ? (
                <span className="text-muted-foreground text-xs tabular-nums">{item.meta}</span>
              ) : null}
            </div>
            <p className="text-muted-foreground text-xs mt-0.5 leading-relaxed">{item.detail}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DivisionTraitsExplainerDialog({
  profile,
  baseline,
  open,
  onOpenChange,
}: DivisionTraitsExplainerDialogProps) {
  const rows = deriveTraitOutcomeRows(profile, baseline);
  const strengths = rows.filter((r) => r.bucket === 'strength');
  const weaknesses = rows.filter((r) => r.bucket === 'weakness');
  const neutralNumeric = rows.filter((r) => r.bucket === 'neutral' && r.kind === 'numeric');
  const neutralBinary = rows.filter((r) => r.bucket === 'neutral' && r.kind === 'binary');

  const strengthItems = strengths.map((s) => ({
    label: s.label,
    detail: s.detail,
    meta: s.percentile !== undefined ? `~${s.percentile}th pct` : undefined,
  }));
  const weaknessItems = weaknesses.map((w) => ({
    label: w.label,
    detail: w.detail,
    meta: w.percentile !== undefined ? `~${w.percentile}th pct` : undefined,
  }));
  const neutralNumericItems = neutralNumeric.map((n) => ({
    label: n.label,
    detail: n.detail,
    meta: n.percentile !== undefined ? `~${n.percentile}th pct` : undefined,
  }));
  const neutralBinaryItems = neutralBinary.map((n) => ({
    label: n.label,
    detail: n.detail,
  }));

  const hasActive = strengthItems.length > 0 || weaknessItems.length > 0;
  const hasNeutral = neutralNumericItems.length > 0 || neutralBinaryItems.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl lg:max-w-4xl w-[calc(100vw-2rem)] max-h-[min(85vh,880px)] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle>Division traits</DialogTitle>
          <DialogDescription asChild>
            <div className="text-left space-y-2 text-muted-foreground text-sm">
              <p>
                Traits compare this division to <strong>all other divisions</strong> in the loaded roster using the
                same briefing metrics.
              </p>
              <p>
                <strong>Numeric:</strong> each value is ranked against every division; roughly the top quarter
                (75th percentile or higher) counts as a strength, the bottom quarter (25th or lower) as a weakness.
                Tank quality uses a composite score when enough MBTs are fielded.
              </p>
              <p>
                <strong>Binary:</strong> a capability is a strength if your deck has it but fewer than 40% of
                divisions do; a weakness if you lack it but more than 60% of divisions have it.
              </p>
              <p>
                <strong>Not flagged</strong> lists metrics that did not cross those thresholds for this deck — so you
                can see what other divisions might show as strengths or weaknesses.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-6 overflow-y-auto flex-1 min-h-0 space-y-6 border-t bg-muted/15">
          {!hasActive && !hasNeutral ? (
            <p className="text-sm text-muted-foreground pt-4">
              No trait rows could be computed (missing baseline data). Slot costs and KPIs above still describe the
              deck.
            </p>
          ) : (
            <>
              {hasActive ? (
                <div className="grid gap-6 sm:grid-cols-2 pt-4">
                  {strengthItems.length > 0 && (
                    <OutcomeBlock title="Strengths (on summary)" items={strengthItems} variant="strength" />
                  )}
                  {weaknessItems.length > 0 && (
                    <OutcomeBlock title="Weaknesses (on summary)" items={weaknessItems} variant="weakness" />
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground pt-4">
                  No strengths or weaknesses on the summary pills for this division versus the baseline — see{' '}
                  <strong>not flagged</strong> below for where this deck sits in the middle band or common ownership.
                </p>
              )}

              {hasNeutral && (
                <div className="space-y-6 pt-2 border-t border-border/50">
                  <p className="text-xs text-muted-foreground">
                    These did not qualify as strengths or weaknesses for <em>this</em> division; other decks may
                    still earn pills on the same metrics.
                  </p>
                  <div className="grid gap-6 lg:grid-cols-2">
                    {neutralNumericItems.length > 0 && (
                      <OutcomeBlock
                        title="Numeric — not flagged (middle band)"
                        items={neutralNumericItems}
                        variant="neutral"
                      />
                    )}
                    {neutralBinaryItems.length > 0 && (
                      <OutcomeBlock
                        title="Binary — not flagged (common or balanced)"
                        items={neutralBinaryItems}
                        variant="neutral"
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
